import { Link } from 'react-router-dom';

import { Avatar, Box } from '@mui/material';

import styled from '@emotion/styled';
import { MRT_ColumnDef } from 'material-react-table';

import {
  BlockedUser,
  UserAccessRequestResultValue,
  VennueAccessRequestItem,
  VenueAccessREnum,
} from '../../../../graphql/API';
import LinkProfile from '../../../../pages/Collabs/components/LinkProfile';
import LoadingButton from '../../../Button/LoadingButton';
import { OnAction } from '../../../List/ListAccess/types';
import StatusLabel from '../../../StatusLabel/StatusLabel';
import { mapToActionRequest } from '../../../Tables/AccessRequest/utils';

const RemoveButton = styled(LoadingButton)({
  borderRadius: '8px',
  color: '#E05757',
  border: '1px solid #E05757',
  ':hover': {
    border: '1px solid #E05757',
  },
});

export const accessRequestsTableColumns = ({
  loadingState,
  onAction,
}: {
  loadingState: Record<string, boolean>;
  onAction: OnAction;
}): MRT_ColumnDef<VennueAccessRequestItem>[] => [
  {
    accessorKey: 'user.picture.thumbnail',
    header: '',
    size: 30,
    enableSorting: false,
    Cell: ({ row }) => (
      <Avatar
        src={String(row.original.user?.picture?.thumbnail)}
        alt={row.original.user?.firstname || ''}
        sx={{ height: '50px', width: '50px' }}
      />
    ),
  },
  {
    accessorKey: 'userName',
    header: 'Name',
    size: 150,
    enableSorting: true,
    Cell: ({
      row: {
        original: { user },
      },
    }) => (
      <LinkProfile
        fontSize='15px'
        url={`/users/${user?.id}`}
        label={`${user?.firstname} ${user?.lastname}`}
        hoverTextDecoration='underline'
      />
    ),
  },
  {
    accessorKey: 'lifestyle',
    header: 'Category',
    size: 200,
    Cell: ({
      row: {
        original: { user },
      },
    }) =>
      user?.lifestyle !== undefined ? (
        <StatusLabel
          type='userCategory'
          status={user?.lifestyle ? 'BP LIFESTYLE' : 'BP SELECT'}
          emptyBackground
          fontWeight={400}
        />
      ) : (
        '-'
      ),
  },
  {
    accessorKey: 'offerTitle',
    header: 'Offer',
    size: 200,
    Cell: ({ row }) => (
      <Box
        component={Link}
        sx={{
          color: '#111',
          textDecoration: 'none',
          ':hover': {
            textDecoration: 'underline',
          },
        }}
        to={`/offers/${row.original.offerID}`}
      >
        {row.original.offer?.title}
      </Box>
    ),
  },
  {
    accessorKey: 'action',
    header: 'Action',
    size: 80,
    enableSorting: false,
    Cell: ({ row }) => (
      <>
        {row.original.status === UserAccessRequestResultValue.accepted ? (
          <RemoveButton
            variant='outlined'
            onClick={() => {
              if (row.original.userID && row.original.offerID) {
                onAction({
                  action: VenueAccessREnum.declined,
                  userID: row.original.userID,
                  offerID: row.original.offerID,
                });
              }
            }}
            loading={loadingState[`${row.original.userID}:${row.original.offerID}`]}
          >
            Remove
          </RemoveButton>
        ) : row.original.status === UserAccessRequestResultValue.declined ? (
          <LoadingButton
            variant='outlined'
            onClick={() => {
              if (row.original.userID && row.original.offerID) {
                onAction({
                  action: VenueAccessREnum.accepted,
                  userID: row.original.userID,
                  offerID: row.original.offerID,
                });
              }
            }}
            loading={loadingState[`${row.original.userID}:${row.original.offerID}`]}
          >
            Undo
          </LoadingButton>
        ) : null}
      </>
    ),
  },
];

export const blockedUsersTableColumns = ({
  venueID,
  loadingState,
  onAction,
}: {
  venueID: number;
  loadingState: Record<string, boolean>;
  onAction: OnAction;
}): MRT_ColumnDef<BlockedUser>[] => [
  {
    accessorKey: 'picture.thumbnail',
    header: '',
    size: 30,
    enableSorting: false,
    Cell: ({ row }) => (
      <Avatar
        src={String(row.original?.picture?.thumbnail)}
        alt={`${row.original?.firstname} ${row.original?.lastname}`}
        sx={{ height: '50px', width: '50px' }}
      />
    ),
  },
  {
    accessorKey: 'userName',
    header: 'Name',
    size: 150,
    enableSorting: true,
    Cell: ({
      row: {
        original: { id, firstname, lastname },
      },
    }) => (
      <LinkProfile
        fontSize='15px'
        url={`/users/${id}`}
        label={`${firstname} ${lastname}`}
        hoverTextDecoration='underline'
      />
    ),
  },
  {
    accessorKey: 'lifestyle',
    header: 'Category',
    size: 200,
    Cell: ({
      row: {
        original: { tags },
      },
    }) =>
      (tags || []).length > 0 ? (
        <StatusLabel
          type='userCategory'
          status={tags?.includes('BP LIFESTYLE') ? 'BP LIFESTYLE' : 'BP SELECT'}
          emptyBackground
          fontWeight={400}
        />
      ) : (
        '-'
      ),
  },
  {
    accessorKey: 'blockedDate',
    header: 'Offer',
    size: 200,
    enableSorting: false,
    Cell: () => <span>All offers</span>,
  },
  {
    accessorKey: 'action',
    header: 'Action',
    size: 80,
    enableSorting: false,
    Cell: ({ row }) => (
      <LoadingButton
        variant='outlined'
        onClick={() => {
          if (row.original.id && venueID) {
            onAction({
              userID: Number(row.original.id),
              venueID: venueID,
            });
          }
        }}
        loading={loadingState[`${row.original.id}:${venueID}`]}
      >
        Unblock
      </LoadingButton>
    ),
  },
];
